import { LogOut, X } from "lucide-react";
import { Avatar } from "./avatar";
import { Brand } from "./brand";
import { cn } from "./cn";
import { navGroups } from "./nav";
import { UiLink, usePathname } from "./router";

/**
 * Who is signed in, as the shell needs to render them. The design read this
 * from its `lib/data` fixture module; here it is a prop all the way down, so
 * nothing under `src/ui/` reaches for a session.
 */
export type AccountSummary = {
  name: string;
  email: string;
  initials: string;
};

function isActive(pathname: string, href: string, nested?: boolean) {
  return nested ? pathname === href || pathname.startsWith(`${href}/`) : pathname === href;
}

export function SidebarContent({
  account,
  badges,
  onNavigate,
  onSignOut,
}: {
  account: AccountSummary;
  badges: Record<string, number>;
  onNavigate?: () => void;
  onSignOut?: () => void;
}) {
  const pathname = usePathname();

  return (
    <div className="flex h-full flex-col bg-sidebar">
      <div className="flex h-topbar items-center px-5">
        <Brand />
      </div>

      <nav className="scrollbar-slim flex-1 overflow-y-auto px-3 pb-4" aria-label="Portal">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-6">
            <p className="mb-2 px-3 text-2xs font-bold tracking-[0.13em] text-ink-4 uppercase">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = isActive(pathname, item.href, item.nested);
                const badge = item.badgeKey ? badges[item.badgeKey] : 0;
                return (
                  <li key={item.href}>
                    <UiLink
                      href={item.href}
                      onClick={onNavigate}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "relative flex items-center gap-3 rounded-field px-3 py-2.5 text-base font-medium transition-colors duration-150",
                        active
                          ? "bg-brand-soft font-semibold text-brand-ink"
                          : "text-ink-2 hover:bg-surface-sunken/70 hover:text-ink",
                      )}
                    >
                      {active ? (
                        <span
                          aria-hidden
                          className="absolute top-1/2 -left-3 h-5 w-[3px] -translate-y-1/2 rounded-r-sm bg-brand"
                        />
                      ) : null}
                      <item.icon
                        className={cn(
                          "size-4 shrink-0",
                          active ? "text-brand" : "text-ink-4",
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {badge ? (
                        <span className="grid min-w-5 place-items-center rounded-pill bg-warning px-1.5 py-0.5 text-2xs font-bold text-white">
                          {badge}
                        </span>
                      ) : null}
                    </UiLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-line/70 p-3">
        <div className="flex items-center gap-3 rounded-field px-2 py-2">
          <Avatar initials={account.initials} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{account.name}</p>
            <p className="truncate text-2xs text-ink-4">{account.email}</p>
          </div>
        </div>
        {/*
          The design signed out by linking to /sign-in, because it had no
          session to end. Here signing out is a mutation the screen owns, so
          the shell takes a callback and renders a button; the link stays as
          the fallback for a shell rendered without one.
        */}
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-field border border-line bg-surface px-3 py-2.5 text-sm font-semibold text-ink-2 transition-colors hover:border-ink-4/40 hover:text-ink"
          >
            <LogOut className="size-3.5" aria-hidden />
            Sign out
          </button>
        ) : (
          <UiLink
            href="/sign-in"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-field border border-line bg-surface px-3 py-2.5 text-sm font-semibold text-ink-2 transition-colors hover:border-ink-4/40 hover:text-ink"
          >
            <LogOut className="size-3.5" aria-hidden />
            Sign out
          </UiLink>
        )}
      </div>
    </div>
  );
}

export function Sidebar({
  account,
  open,
  onClose,
  badges,
  onSignOut,
}: {
  account: AccountSummary;
  open: boolean;
  onClose: () => void;
  badges: Record<string, number>;
  onSignOut?: () => void;
}) {
  return (
    <>
      {/* Fixed rail — lg and up */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-sidebar border-r border-line lg:block">
        <SidebarContent account={account} badges={badges} onSignOut={onSignOut} />
      </aside>

      {/* Off-canvas drawer — below lg */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          open ? "pointer-events-auto" : "pointer-events-none",
        )}
        aria-hidden={!open}
      >
        <div
          onClick={onClose}
          className={cn(
            "absolute inset-0 bg-ink/40 backdrop-blur-[2px] transition-opacity duration-200",
            open ? "opacity-100" : "opacity-0",
          )}
        />
        <aside
          role="dialog"
          aria-modal={open}
          aria-label="Portal navigation"
          className={cn(
            "absolute inset-y-0 left-0 w-[17rem] max-w-[85vw] border-r border-line shadow-float transition-transform duration-250 ease-out-soft",
            open ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="absolute top-4 right-3 z-10 grid size-8 place-items-center rounded-full text-ink-3 hover:bg-surface-sunken"
          >
            <X className="size-4" aria-hidden />
          </button>
          <SidebarContent
            account={account}
            badges={badges}
            onNavigate={onClose}
            onSignOut={onSignOut}
          />
        </aside>
      </div>
    </>
  );
}
